import naturalCompare from 'string-natural-compare'
import category from '../category'
import product from '.'
import loader from '../../partials/loader'
import searchHelper from '../search/searchHelper'

const alternatives = (data) => ({
    ...loader,
    loading: false,
    currentIso: window.accuvars.language.iso_code,
    product: null,
    filters: {},
    alternatives: null,
    initialLoad: true,
    selectedAlternatives: {},
    productId: data.productId,
    categoryId: data.categoryId,
    categoryLink: data.categoryLink,
    storedCategoryFilters: searchHelper.storedCategoryFilters,
    init() {
        this.populateAlternatives()
    },
    async populateAlternatives() {
        const combinedQueries = [
            // Query 1: Load product data
            {
                indexName: 'new_products',
                query: '',
                params: {
                    hitsPerPage: 1,
                    page: 0,
                    facetFilters: `objectID:${this.productId}`,
                },
            },
            // Query 2: Load category data
            {
                indexName: 'new_product_categories',
                query: '',
                params: {
                    hitsPerPage: 1,
                    page: 0,
                    facetFilters: `objectID:${this.categoryId}`,
                },
            },
        ]

        try {
            var results = await window.api.search(combinedQueries)
        } catch (error) {
            console.error('Error loading product or category data:', error)
            this.initialLoad = false
            return
        }

        if (!results[0] || !results[0].nbHits) {
            console.warn('Unknown product')
            this.initialLoad = false
            return
        }

        if (!results[1] || !results[1].nbHits) {
            this.initialLoad = false
            return
        }

        this.product = results[0].hits[0]
    
        const categoryFilters = results[1]?.hits?.[0]?.[this.currentIso]?.main_features
        if (!categoryFilters) {
            this.initialLoad = false
            return
        }

        this.filters = Object.values(categoryFilters)
            .map(f => f.name)
            .reduce((acc, filterName) => {
                const value =
                    this.product[this.currentIso]?.features?.[filterName]
                if (value != null) {
                    return { ...acc, [filterName]: value }
                }
                return acc
            }, {})
        if (Object.keys(this.filters).length === 0) {
            this.initialLoad = false
            return
        }

        window.api
            .search(this.generateQueriesForAlternatives())
            .then(searchResults => {
                this.alternatives = Object.values(categoryFilters)
                    .filter(c => this.filters[c.name] != null)
                    .map(c => {
                        const searchResult =
                            searchResults[
                                Object.keys(this.filters).indexOf(c.name)
                            ]
                        const facetKey = `${this.currentIso}.features.${c.name}`
                        const facets = searchResult?.facets?.[facetKey] || {}

                        const alternatives = Object.entries(facets)
                            .map(item => ({
                                name: item[0],
                                count: item[1],
                                isCurrent: this.filters[c.name]?.toLowerCase() === item[0]?.toLowerCase(),
                            }))
                            .sort((a, b) => naturalCompare(a.name, b.name))

                        return {
                            label: c.name,
                            alternatives,
                        }
                    })
                    .filter(alternative => alternative.alternatives.length > 0)

                this.alternatives.forEach(a => {
                    if (a.alternatives.length > 0) {
                        const currentAlternative = a.alternatives.find(
                            alt => alt.isCurrent,
                        )
                        if (currentAlternative) {
                            this.selectedAlternatives[a.label] =
                                currentAlternative.name
                        }
                    }
                })

                this.initialLoad = false
            })
            .catch(error => {
                console.error('Error loading alternatives:', error)
                this.initialLoad = false
            })
    },

    redirectToProduct() {
        this.startLoader()
        const validFilters = Object.entries(this.selectedAlternatives)
            .filter(([_, value]) => value != null)
            .map(([key]) => key)

        window.api
            .search([
                {
                    indexName: 'new_products',
                    query: '',
                    params: {
                        hitsPerPage: 2,
                        page: 0,
                        facetFilters: [
                            [`category_id:${this.product.category_id}`],
                            ...validFilters.map(
                                f =>
                                    `${this.currentIso}.features.${f}: ${this.selectedAlternatives[f]}`,
                            ),
                        ],
                    },
                },
            ])
            .then(searchResults => {
                const resultCount = searchResults[0]?.nbHits || 0

                if (resultCount === 0) {
                    this.removeCategoryFilters()
                    window.location.href = this.categoryLink
                } else if (resultCount > 2) {
                    const url = new URL(this.categoryLink)

                    validFilters.forEach(f => {
                        url.searchParams.append(f, this.selectedAlternatives[f])
                    })

                    window.location.href = url
                } else {
                    const firstHit = searchResults[0]?.hits?.[0]
                    if (firstHit?.[this.currentIso]?.uri) {
                        window.location.href = firstHit[this.currentIso].uri
                    } else {
                        this.removeCategoryFilters()
                        window.location.href = this.categoryLink
                    }
                }
            })
            .catch(error => {
                console.error('Search error:', error)
                this.stopLoader()
            })
    },

    generateQueriesForAlternatives() {
        return Object.keys(this.filters).map(f =>
            this.generateAlternativeQuery(f),
        )
    },

    generateAlternativeQuery(filterName) {
        const validFilters = Object.entries(this.filters)
            .filter(([key, value]) => key !== filterName && value != null)
            .map(
                ([key, value]) =>
                    `${this.currentIso}.features.${key}: ${value}`,
            )
        return {
            indexName: 'new_products',
            query: '',
            params: {
                hitsPerPage: 1,
                page: 0,
                facetFilters: [
                    [`category_id:${this.product.category_id}`],
                    ...validFilters,
                ],
                facets: [`${this.currentIso}.features.${filterName}`],
            },
        }
    },

    removeCategoryFilters() {
        this.storedCategoryFilters.active_filters =
            this.storedCategoryFilters.active_filters.filter(
                item => item.category !== this.product.category_id,
            )
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('alternatives', alternatives)
})
